/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, inject, signal } from '@angular/core';
import { Observable, throwError, map } from 'rxjs';
import {
  HttpService,
  TranslationService,
  AuthorizationStore,
  ConfiguracionRolService,
  leerContextoDeProducto,
  leerDeLaPestana,
} from '@nebula-shared/data-access';
import { SidebarMenuItem } from '../../../domain/src/lib/sidebar.model';

/** El producto con el que se armo el menu, tal como lo trae el servicio de administracion. */
export interface ProductoActivo {
  /** Nombre del producto (puede ser una clave de traduccion). */
  nombre: string;
  /** Descripcion del producto (puede ser una clave de traduccion). */
  descripcion: string;
}

@Injectable({ providedIn: 'root' })
export class SidebarService {
  private readonly http = inject(HttpService);
  private readonly translation = inject(TranslationService);
  private readonly authorizationStore = inject(AuthorizationStore);
  private readonly configuracionRol = inject(ConfiguracionRolService);
  private readonly MENU_ENDPOINT = '/simappe-admin/api/v1/menu';

  /**
   * Producto activo: sale del mismo menu que lo filtra, sin otra llamada. Nulo mientras no hay
   * producto o su menu no ha llegado; el sidebar del perfil 3.0 lo muestra en su cabecera.
   */
  private readonly producto = signal<ProductoActivo | null>(null);
  readonly productoActivo = this.producto.asReadonly();

  /**
   * Mapa segmento-de-ruta -> clave de traduccion (optionName/optionGroupName), independiente del
   * idioma. Se llena al construir el menu y permite re-traducir titulos de pestañas abiertas al
   * cambiar idioma (el {@link NebulaNavigationService} del kit expone `tabs` como señal read-only).
   */
  readonly routeKeyMap = new Map<string, string>();

  /**
   * Mapa segmento-de-ruta -> clave del GRUPO de menu al que pertenece la opcion.
   *
   * El grupo no aparece en la ruta (`/layout/accounting/exchange-rate/list` no
   * lo nombra), pero el breadcrumb lo necesita para mostrar el camino completo.
   * Se llena del mismo recorrido del menu que llena {@link routeKeyMap}.
   */
  readonly routeGroupMap = new Map<string, string>();

  /**
   * Mapa segmento-de-ruta -> ICONO del GRUPO de menu al que pertenece la opcion.
   * Se llena del mismo recorrido del menu que llena {@link routeGroupMap}.
   */
  readonly routeGroupIconMap = new Map<string, string>();

  /**
   * Mapa segmento-de-ruta -> clave i18n del `ai_context` de la opcion (`optionConfig.ai_context`
   * del menu), el contexto que Nebulina manda a la IA. Solo tiene las opciones que lo declaran.
   */
  private readonly routeAiContextMap = new Map<string, string>();

  /**
   * Mapa segmento-de-ruta -> fecha de entrega planeada (`optionConfig.disponible_desde`, AAAA-MM-DD)
   * de una opcion en construccion. Solo tiene las opciones que la declaran.
   */
  private readonly routeDisponibleDesdeMap = new Map<string, string>();

  /**
   * Mapa segmento-del-modulo (`moduleUrl` sin barra, p. ej. `payroll`) -> clave i18n de su nombre
   * (`moduleName`, p. ej. `MODULES_PAYROLL_NAME`). El breadcrumb nombra el modulo con ella.
   */
  private readonly moduloKeyMap = new Map<string, string>();

  /**
   * Cuenta las veces que el menu lleno los mapas de segmentos. Los mapas no son reactivos: quien
   * pinte con ellos (el breadcrumb) lee esta señal para volver a resolver cuando el menu llega.
   */
  private readonly mapasDelMenu = signal(0);
  readonly rutasDelMenu = this.mapasDelMenu.asReadonly();

  /** Claves de localStorage donde sobreviven los mapas entre recargas. */
  private static readonly ROUTE_KEYS_CACHE = 'nebula_route_keys';
  private static readonly ROUTE_GROUPS_CACHE = 'nebula_route_groups';
  private static readonly ROUTE_GROUPS_ICON_CACHE = 'nebula_route_groups_icon';

  constructor() {
    this.restoreRouteKeys();
    this.restoreRouteGroupIcons();
  }

  /** Segmentos de accion/ruta que NO identifican la opcion (se ignoran al derivar la clave). */
  private readonly ROUTE_ACTION_SEGMENTS = new Set([
    'list',
    'create',
    'edit',
    'view',
    'detail',
    'new',
    'layout',
  ]);

  /** Ultimo segmento significativo de una ruta (ignora acciones e ids). Ej: '/layout/accounting/tipo-plan/list' -> 'tipo-plan'. */
  routeKeySegment(route: string | undefined): string {
    if (!route) return '';
    const parts = route
      .split('/')
      .map((p) => p.trim())
      .filter(
        (p) =>
          p && !this.ROUTE_ACTION_SEGMENTS.has(p) && !/^\d+$/.test(p) && !/^[0-9a-f]{8}-/.test(p),
      );
    return parts.length ? parts[parts.length - 1] : '';
  }

  /** Clave de traduccion asociada al segmento significativo de una ruta (si esta mapeada). */
  keyForRoute(route: string | undefined): string | undefined {
    const seg = this.routeKeySegment(route);
    return seg ? this.routeKeyMap.get(seg) : undefined;
  }

  /**
   * Clave del grupo de menu al que pertenece la opcion de `route`.
   *
   * Una opcion puede figurar en mas de un grupo; se responde el ultimo que la
   * declaro al recorrer el menu, que es un criterio estable mientras el menu
   * no cambie. Devuelve undefined para rutas que no son opciones de menu.
   */
  groupKeyForRoute(route: string | undefined): string | undefined {
    const seg = this.routeKeySegment(route);
    return seg ? this.routeGroupMap.get(seg) : undefined;
  }

  /**
   * Si al tramo que sigue al modulo se le insertara su grupo de menu en el breadcrumb.
   *
   * <p>Los armadores del breadcrumb descartan un tramo que repite el rotulo del anterior. Contra el
   * modulo no aplica cuando la opcion tiene grupo: el grupo va en medio, y la opcion que se llama
   * como su modulo (Facturacion › Operaciones › Facturacion) se perderia.</p>
   *
   * @param segmento tramo de la ruta.
   * @param tramosPrevios cuantos tramos lleva armados el breadcrumb.
   * @returns true si el tramo debe conservarse aunque repita el rotulo del modulo.
   */
  /**
   * Clave i18n del `ai_context` de la opcion de `route`, si su configuracion lo declara.
   *
   * @param route ruta de la opcion o de una de sus acciones
   * @returns la clave, o undefined si la opcion no la declara
   */
  aiContextForRoute(route: string | undefined): string | undefined {
    const seg = this.routeKeySegment(route);
    return seg ? this.routeAiContextMap.get(seg) : undefined;
  }

  /**
   * Fecha de entrega planeada de la opcion en construccion de `route`, si su configuracion la declara.
   *
   * @param route ruta de la opcion
   * @returns la fecha AAAA-MM-DD, o undefined si la opcion no la declara
   */
  disponibleDesdeForRoute(route: string | undefined): string | undefined {
    const seg = this.routeKeySegment(route);
    return seg ? this.routeDisponibleDesdeMap.get(seg) : undefined;
  }

  /**
   * Clave i18n del nombre del modulo que abre `url` (`/layout/<modulo>/...`), segun el menu.
   *
   * @param url url abierta
   * @returns la clave `MODULES_<X>_NAME`, o undefined si el menu no trae ese modulo
   */
  moduleKeyForUrl(url: string | undefined): string | undefined {
    const partes = (url ?? '').split(/[?#]/)[0].split('/').filter(Boolean);
    const i = partes.indexOf('layout');
    const segmento = partes[i + 1];
    return segmento ? this.moduloKeyMap.get(decodeURIComponent(segmento)) : undefined;
  }

  conservaTrasElModulo(segmento: string, tramosPrevios: number): boolean {
    return tramosPrevios === 1 && this.routeGroupMap.has(segmento);
  }

  /**
   * Icono del grupo de menu al que pertenece la opcion de `route`.
   */
  groupIconForRoute(route: string | undefined): string | undefined {
    const seg = this.routeKeySegment(route);
    return seg ? this.routeGroupIconMap.get(seg) : undefined;
  }

  getMenu(): Observable<SidebarMenuItem[]> {
    const userDataJson = leerDeLaPestana('user_data');
    if (!userDataJson) return throwError(() => new Error('Sin sesión'));

    const selectedProductId = leerContextoDeProducto('selectedProductId');

    const userData = JSON.parse(userDataJson);
    const roleIds: number[] = userData.roles?.map((r: { id: string }) => r.id) || [];
    if (!roleIds.length) return throwError(() => new Error('Sin roles'));

    this.authorizationStore.load().subscribe();

    return this.http.post<any>(`${this.MENU_ENDPOINT}/read-by-roles-grouped`, roleIds).pipe(
      map((response: any) => {
        const content = response?.response?.content;

        const items = Array.isArray(content) ? content : [];

        if (!items.length) return [];

        const filteredItems = this.filterByApplicationId(items, selectedProductId);
        // El codigo del producto activo sale del mismo menu que lo filtra: asi una sesion que ya
        // traia el producto elegido lo recupera (HT_ARQ_012, parametros por producto).
        if (selectedProductId) {
          this.configuracionRol.fijarProductoActivo(
            filteredItems[0]?.applications?.[0]?.applicationCode ?? null,
          );
        }
        const aplicacion = filteredItems[0]?.applications?.[0];
        this.producto.set(
          aplicacion
            ? {
                nombre: aplicacion.applicationName ?? '',
                descripcion: aplicacion.applicationDescription ?? '',
              }
            : null,
        );

        return this.buildMenuTree(filteredItems);
      }),
    );
  }

  /**
   * Deja del menu solo las aplicaciones del producto activo.
   *
   * <p>Sin producto elegido devolvia el menu de TODOS los productos. Ninguna opcion debe salir
   * sin producto: las de los demas productos apuntan a rutas que no existen bajo el modulo
   * activo y la pestaña se abre en blanco. Ahora no se devuelve nada, que es lo que corresponde
   * cuando aun no hay producto con el que filtrar (Mantis #285).</p>
   *
   * @param items             menu por rol tal como llega del servicio de administracion.
   * @param selectedProductId producto activo, o nulo si todavia no se ha elegido.
   * @returns el menu acotado al producto activo; vacio si no hay producto.
   */
  private filterByApplicationId(items: any[], selectedProductId: string | null): any[] {
    if (!selectedProductId) {
      return [];
    }

    const filteredItems: any[] = [];

    items.forEach((role) => {
      if (!role.applications) return;

      const filteredApplications = role.applications.filter(
        (app: { applicationId: { toString: () => string } }) =>
          app.applicationId?.toString() === selectedProductId,
      );

      if (filteredApplications.length > 0) {
        filteredItems.push({
          ...role,
          applications: filteredApplications,
        });
      }
    });

    return filteredItems;
  }

  private buildMenuTree(items: any[]): SidebarMenuItem[] {
    const menuItems: SidebarMenuItem[] = [];
    // Un mismo grupo puede llegar en varios roles del usuario, cada uno con SUS opciones (p. ej.
    // DEVELOPER y DB_ARQUITECTO en Seguridad): el grupo se pinta una vez con la union de opciones.
    const grupos = new Map<string, { item: SidebarMenuItem; rutas: Set<string> }>();

    // El menu recien leido es la unica fuente: lo que quedo en cache de un menu anterior (un
    // grupo que ya no existe, una url que se quito) no debe sobrevivir y torcer el breadcrumb.
    this.routeKeyMap.clear();
    this.routeGroupMap.clear();
    this.routeGroupIconMap.clear();
    this.routeDisponibleDesdeMap.clear();
    this.moduloKeyMap.clear();

    items.forEach((role) => {
      if (!role.applications) return;

      role.applications.forEach((app: any) => {
        if (!app.modules) return;

        app.modules.forEach((module: any) => {
          this.registerModulo(module.moduleUrl, module.moduleName);
          if (!module.optionGroups?.length) return;

          module.optionGroups.forEach((group: any) => {
            const groupKey = `${group.optionGroupId}`;
            const groupIcon = this.getIconName(group.optionGroupAhrefIconClass);
            let grupo = grupos.get(groupKey);
            if (!grupo) {
              grupo = {
                item: {
                  label: this.getLabel(group.optionGroupName),
                  icon: groupIcon,
                  route: group.optionGroupUrl,
                  children: [],
                  expanded: false,
                },
                rutas: new Set<string>(),
              };
              grupos.set(groupKey, grupo);
              this.registerRouteKey(group.optionGroupUrl, group.optionGroupName);
              menuItems.push(grupo.item);
            }

            (group.options ?? []).forEach((opt: any) => {
              const optRoute = opt.optionFullPath || opt.optionPath;
              if (grupo.rutas.has(optRoute)) return;
              grupo.rutas.add(optRoute);
              grupo.item.children!.push({
                label: this.getLabel(opt.optionName),
                icon: this.getIconName(opt.optionAhrefIconClass),
                route: optRoute,
                children: [],
              });
              this.registerRouteKey(optRoute, opt.optionName);
              this.registerRouteGroup(optRoute, group.optionGroupName);
              this.registerRouteGroupIcon(optRoute, groupIcon);
              this.registerRouteAiContext(optRoute, opt.optionConfig?.ai_context);
              this.registerRouteDisponibleDesde(optRoute, opt.optionConfig?.disponible_desde);
            });
          });
        });
      });
    });

    this.persistRouteKeys();
    this.persistRouteGroupIcons();
    this.mapasDelMenu.update((n) => n + 1);
    return menuItems;
  }

  private getIconName(iconValue: string | undefined): string {
    if (!iconValue) return 'Folder';
    return iconValue;
  }

  /** Asocia el segmento significativo de una ruta con su clave de traduccion (optionName/group). */
  private registerRouteKey(route: string | undefined, key: string | undefined): void {
    if (!key) return;
    const seg = this.routeKeySegment(route);
    if (seg) this.routeKeyMap.set(seg, key);
  }

  /** Asocia el segmento significativo de una ruta con la clave del grupo que la contiene. */
  private registerRouteGroup(route: string | undefined, groupKey: string | undefined): void {
    if (!groupKey) return;
    const seg = this.routeKeySegment(route);
    if (seg) this.routeGroupMap.set(seg, groupKey);
  }

  /** Asocia el segmento significativo de una ruta con el icono del grupo que la contiene. */
  private registerRouteGroupIcon(route: string | undefined, icon: string | undefined): void {
    if (!icon) return;
    const seg = this.routeKeySegment(route);
    if (seg) this.routeGroupIconMap.set(seg, icon);
  }

  /** Asocia el segmento significativo de una ruta con la clave del `ai_context` de su opcion. */
  private registerRouteAiContext(route: string | undefined, clave: unknown): void {
    if (typeof clave !== 'string' || !clave) return;
    const seg = this.routeKeySegment(route);
    if (seg) this.routeAiContextMap.set(seg, clave);
  }

  /** Asocia el segmento significativo de una ruta con la fecha de entrega planeada de su opcion. */
  private registerRouteDisponibleDesde(route: string | undefined, fecha: unknown): void {
    if (typeof fecha !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(fecha)) return;
    const seg = this.routeKeySegment(route);
    if (seg) this.routeDisponibleDesdeMap.set(seg, fecha);
  }

  /** Asocia el segmento de la url de un modulo (`/payroll` -> `payroll`) con la clave de su nombre. */
  private registerModulo(url: string | undefined, clave: string | undefined): void {
    const segmento = decodeURIComponent(url ?? '')
      .split('/')
      .filter(Boolean)
      .pop();
    if (segmento && clave) this.moduloKeyMap.set(segmento, clave);
  }

  /** Rehidrata el mapa de iconos de grupos. */
  private restoreRouteGroupIcons(): void {
    this.restoreMap(SidebarService.ROUTE_GROUPS_ICON_CACHE, this.routeGroupIconMap);
  }

  /** Persiste el mapa de iconos de grupos. */
  private persistRouteGroupIcons(): void {
    this.persistMap(SidebarService.ROUTE_GROUPS_ICON_CACHE, this.routeGroupIconMap);
  }

  private getLabel(key: string | undefined): string {
    if (!key) return '';
    const translated = this.translation.translate(key);
    if (translated && translated !== key) return translated;
    return this.formatKey(key);
  }

  /**
   * Etiqueta de un segmento de ruta, para el breadcrumb.
   *
   * Resuelve en tres pasos, todos contra el bundle:
   *  1. la clave de la opcion de menu registrada para ese segmento, que es la
   *     misma etiqueta que muestra el sidebar (asi no hacen falta claves
   *     paralelas para lo que el menu ya nombra);
   *  2. `BC.<segmento>` para lo que no es una opcion (modulos y acciones como
   *     list/create/edit);
   *  3. el segmento formateado, cuando ninguna de las dos esta traducida.
   */
  labelForSegment(segment: string): string {
    if (!segment) return '';

    const optionKey = this.routeKeyMap.get(segment);
    if (optionKey) {
      const porMenu = this.translation.translate(optionKey);
      if (porMenu && porMenu !== optionKey) return porMenu;
    }

    if (/^\d+$/.test(segment) || /^[0-9a-f]{8}-/.test(segment)) {
      return this.translateOr('BC.detail', 'Detalle');
    }

    return this.rotuloSinMenu(segment);
  }

  /**
   * Rotulo de un segmento que el menu no nombra: `BC.<segmento>` si el bundle lo trae, o el
   * segmento con mayuscula inicial. Es el que lleva una opcion mientras su menu no ha llegado.
   *
   * @param segment tramo de la ruta.
   * @return el rotulo provisional del tramo.
   */
  rotuloSinMenu(segment: string): string {
    return this.translateOr(`BC.${segment}`, this.formatSegment(segment));
  }

  /** Traduccion de `key`, o `alterno` si esa clave no esta en el bundle. */
  private translateOr(key: string, alterno: string): string {
    const value = this.translation.translate(key);
    return value !== key ? value : alterno;
  }

  /** 'Módulo_de_pagos' -> 'Módulo De Pagos': mayuscula solo al inicio de palabra, tildes incluidas. */
  private formatKey(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/(^|\s)(\S)/g, (_, borde: string, c: string) => borde + c.toUpperCase());
  }

  /** 'accounting-currency' -> 'Accounting Currency'. */
  private formatSegment(segment: string): string {
    return segment
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  /** Rehidrata los mapas de segmentos para que el breadcrumb no dependa de que el menu ya haya cargado. */
  private restoreRouteKeys(): void {
    this.restoreMap(SidebarService.ROUTE_KEYS_CACHE, this.routeKeyMap);
    this.restoreMap(SidebarService.ROUTE_GROUPS_CACHE, this.routeGroupMap);
  }

  private restoreMap(cacheKey: string, destino: Map<string, string>): void {
    try {
      const raw = localStorage.getItem(cacheKey);
      if (!raw) return;
      const entries = JSON.parse(raw) as [string, string][];
      entries.forEach(([seg, key]) => destino.set(seg, key));
    } catch {
      /* cache ilegible: se repuebla al cargar el menu */
    }
  }

  private persistRouteKeys(): void {
    this.persistMap(SidebarService.ROUTE_KEYS_CACHE, this.routeKeyMap);
    this.persistMap(SidebarService.ROUTE_GROUPS_CACHE, this.routeGroupMap);
  }

  private persistMap(cacheKey: string, origen: Map<string, string>): void {
    try {
      localStorage.setItem(cacheKey, JSON.stringify([...origen.entries()]));
    } catch {
      /* cuota de localStorage: el mapa sigue vivo en memoria */
    }
  }
}
